import React, { useState, useEffect, memo } from 'react';
import { useAuth } from '../../context/auth';
import { useLocation, Navigate } from 'react-router-dom';
import dashboardScreenshot from '../../assets/images/dashboard-screenshot.png';
import safeMccLogo from '../../assets/images/safemcc-logo-v2.png'; // Updated logo import
import safeMCCfrontPage from '../../assets/images/safemcc-frontpage.png';

// Optimized GoogleSignInButton component with new styling
const GoogleSignInButton = memo(({ onClick, isLoading }) => (
	<button
		onClick={onClick}
		disabled={isLoading}
		className="w-full mb-4 flex items-center justify-center gap-2 bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 shadow-sm"
	>
		<svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
			{/* SVG path remains the same */}
			<g transform="matrix(1, 0, 0, 1, 0, 0)">
				<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#FFFFFF" />
				<path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#FFFFFF" />
				<path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FFFFFF" />
				<path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#FFFFFF" />
			</g>
		</svg>
		<span>Sign in with Google</span>
	</button>
));

const LoginPage = () => {
	const location = useLocation();
	const [error, setError] = useState('');
	const { loginWithGoogle, isAuthenticated } = useAuth();
	const [isLoading, setIsLoading] = useState(false);

	if (isAuthenticated) {
		return <Navigate to="/dashboard" replace />;
	}

	const handleGoogleSignIn = async () => {
		if (isLoading) return;

		setError('');
		setIsLoading(true);

		try {
			const result = await loginWithGoogle();
			if (!result.success) {
				setError(result.error);
			}
			// Firebase auth handles redirect on success
		} catch (error) {
			setError('Failed to sign in with Google');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen w-full flex bg-white">
			{/* Left side with dashboard preview */}
			<div className="hidden lg:flex w-2/5 bg-gradient-to-r from-[#242525] to-[#2D2F45] items-center justify-center p-12">
				<img 
					src={safeMCCfrontPage}
					alt="Dashboard Preview"
					className="max-w-full lg:w-9/10 h-auto rounded-lg shadow-2xl"
				/>
			</div>

			{/* Right side with login form */}
			<div className="w-full lg:w-3/5 flex flex-col items-center justify-center p-8 relative">
				<div className="w-full max-w-md space-y-8">
					<div className="text-left">
						<img src={safeMccLogo} alt="SafeMCC Logo" className="h-16 w-auto mb-8" />
						<h2 className="text-xl text-gray-600 mt-4">
							One-stop platform for all user management and access control needs.
						</h2>
					</div>
					
					{error && (
						<div className="p-3 mb-4 bg-red-100 border border-red-200 text-red-700 rounded-md">
							{error}
						</div>
					)}

					<GoogleSignInButton onClick={handleGoogleSignIn} isLoading={isLoading} />

					<p className="text text-center text-gray-500">
						By signing in, you agree to our{' '}
						<a href="https://s360digital.com/da/privatlivspolitik/" className="font-medium text-indigo-600 hover:text-indigo-500">
							Privacy Policy
						</a>
						.
					</p>
				</div>
				<div className="absolute bottom-8 px-8 text-center text-sm text-gray-500">
					<p className="leading-relaxed">SafeMCC is a centralized access governance platform that allows users to seamlessly manage, audit, monitor and request access to client's accounts across various marketing platforms with their own work credentials. All access management operations are securely authenticated & logged with granularity to allow for easy auditing.</p>
					<p className="mt-4 py-4">&copy; {new Date().getFullYear()} s360. All rights reserved</p>
				</div>
			</div>
		</div>
	);
};

export default memo(LoginPage);
